import React, { useEffect, useState } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import TextInput from '../../../components/TextFields/TextInput.js';
import DateInput from '../../../components/TextFields/DateInput.js';
import NumberInput from '../../../components/TextFields/NumberInput.js';
import BeneficiariesSharesInputs from './BeneficiariesSharesInputs.js';
import PasswordConfirmation from '../PasswordConfirmation.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
import { beneficiariesOptions, optionsWithSpouse } from '../../../services/selectFieldOptions.js';
import validateBeneficiaryForm from './validations/validateBeneficiaryForm.js';
import validateSignage from '../validations/validateSignage.js';
import getCurrentBeneficiaries from './getCurrentBeneficiaries.js';
import { setBeneficiaryData, initialErrorState, initialSignageState, initialSignageErrorState, beneficiariesSharesInit } from '../common/stateInitialization.js';

/**
 * BeneficiaryModal component handles the modal for adding/editing a beneficiary.
 * @param {Object} props - Properties passed to the component.
 * @returns {JSX.Element} - Rendered component.
 */
const BeneficiaryModal = ({
  open,
  loader,
  isContingent,
  beneficiaryData,
  onClose,
  isEdit,
  primaryBeneficiaries,
  contingentBeneficiaries,
  trustee,
  editBeneficiary,
  addBeneficiary,
  isAdmin,
  openChangeRequestForm
}) => {
  const [beneficiary, setBeneficiary] = useState(setBeneficiaryData(beneficiaryData, isEdit));
  const [errorState, setErrorState] = useState(initialErrorState);
  const [signage, setSignage] = useState(initialSignageState);
  const [signageErrorState, setSignageErrorState] = useState(initialSignageErrorState);
  const [beneficiaryShares, setBeneficiaryShares] = useState({
    primaryBeneficiaries: [],
    contingentBeneficiaries: []
  });
  const [passwordConfirmationModalOpen, setPasswordConfirmationModalOpen] = useState(false);
  const {
    firstnameError,
    lastnameError,
    sharesError,
    birthdateError,
    typeError,
    relationshipError
  } = errorState;

  // Destructuring beneficiary data for easy access
  const {
    firstname,
    lastname,
    beneficiaryID,
    type,
    shares,
    birthdate,
    relationship
  } = beneficiary;
  useEffect(() => {
    setBeneficiary(setBeneficiaryData(beneficiaryData, isEdit));
    setBeneficiaryShares(beneficiariesSharesInit(primaryBeneficiaries, contingentBeneficiaries, beneficiaryID));
  }, [beneficiaryData, isEdit]);

  /**
   * Closes the modal and resets form states.
   */
  const close = () => {
    setBeneficiary(setBeneficiaryData(beneficiary));
    setErrorState(initialErrorState);
    setPasswordConfirmationModalOpen(false);
    setSignageErrorState(initialSignageErrorState);
    setSignage(initialSignageState);
    onClose();
  };

  /**
   * Handles shares change for beneficiaries.
   * @param {string} selectedBeneficiaryID - ID of the beneficiary whose shares are being updated.
   * @param {number} value - New shares value.
   */
  const handleSharesChange = (selectedBeneficiaryID, value) => {
    setBeneficiaryShares({
      primaryBeneficiaries: beneficiaryShares.primaryBeneficiaries.map(primaryBeneficiary => {
        if (primaryBeneficiary.beneficiaryID === selectedBeneficiaryID) {
          return {
            ...primaryBeneficiary,
            shares: value
          };
        }
        return primaryBeneficiary;
      }),
      contingentBeneficiaries: beneficiaryShares.contingentBeneficiaries.map(contingentBeneficiary => {
        if (contingentBeneficiary.beneficiaryID === selectedBeneficiaryID) {
          return {
            ...contingentBeneficiary,
            shares: value
          };
        }
        return contingentBeneficiary;
      })
    });
  };

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleInputChange = (field, value) => {
    setBeneficiary(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleSignageChange = (field, value) => {
    setSignage(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Resets the form to its initial state.
   */
  const resetForm = () => {
    setBeneficiaryData(beneficiaryData, false);
    setErrorState(initialErrorState);
    setBeneficiaryShares({
      primaryBeneficiaries,
      contingentBeneficiaries
    });
  };

  /**
   * Validates the form data before submission.
   * @returns {Promise<boolean>} - True if validation passes, false otherwise.
   */
  const validateFormData = async () => {
    const formErrors = await validateBeneficiaryForm({
      ...beneficiary,
      trustee
    }, beneficiaryShares, isContingent, beneficiaryID);
    if (formErrors !== false) {
      setErrorState({
        ...formErrors
      });
      return false;
    }
    const translatedBeneficiaryType = localizationMaps.toEnglish('beneficiariesOptions', beneficiary.type);

    // Member requires change request for irrevocable 
    if (isAdmin || translatedBeneficiaryType === "Irrevocable") {
      const beneficiaries = getCurrentBeneficiaries(beneficiaryShares, beneficiaryID);
      const changeRequestData = {
        type: isEdit ? `Edit ${translatedBeneficiaryType} Beneficiary` : `Add ${translatedBeneficiaryType} Beneficiary`,
        details: {
          ...(isEdit && {
            beneficiaryID
          }),
          firstname,
          lastname,
          relationship: localizationMaps.toEnglish("relationships", relationship),
          type: translatedBeneficiaryType,
          shares,
          birthdate: dates.formatOutgoingDate(birthdate),
          primary: beneficiaries.primary,
          contingent: beneficiaries.contingent
        }
      };
      openChangeRequestForm(changeRequestData);
      close();
      resetForm();
      return false;
    }
    return setPasswordConfirmationModalOpen(true);
  };

  /**
   * Saves beneficiary data and signage information.
   * @returns {boolean} - True if saving is successful, false otherwise.
   */
  const save = async () => {
    const formErrors = await validateSignage({
      ...signage
    });
    if (formErrors !== false) {
      setSignageErrorState({
        ...formErrors
      });
      return false;
    }
    const beneficiaries = getCurrentBeneficiaries(beneficiaryShares, beneficiaryID);
    let success = false;
    const data = {
      firstname,
      lastname,
      relationship: localizationMaps.toEnglish("relationships", relationship),
      type: localizationMaps.toEnglish("beneficiariesOptions", type),
      shares,
      birthdate: dates.formatOutgoingDate(birthdate),
      password: signage.password,
      signage: {
        name: signage.name,
        signature: signage.signature,
        acceptedTerms: signage.acceptedTerms,
        timestampOfAcceptance: dates.getUTC(),
        datedOn: dates.formatOutgoingDate(signage.datedOn)
      }
    };
    if (isEdit) {
      success = await editBeneficiary(beneficiaryID, data, beneficiaries, isContingent);
    } else {
      success = await addBeneficiary(data, beneficiaries, isContingent);
    }
    if (success) {
      window.location.reload();
      close();
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Modal, {
    testid: "add-edit-beneficiary-modal",
    loader: loader,
    open: open,
    onClose: close,
    onBack: passwordConfirmationModalOpen ? () => setPasswordConfirmationModalOpen(false) : null,
    title: isEdit ? i18n.t(tokens.titles["edit beneficiary"]) : `${isContingent ? i18n.t(tokens.buttons["add contingent beneficiary"]) : i18n.t(tokens.titles["add beneficiary"])}`,
    primaryButton: {
      label: passwordConfirmationModalOpen ? i18n.t(tokens.buttons.finish) : i18n.t(tokens.buttons["save beneficiary"]),
      onClick: passwordConfirmationModalOpen ? save : validateFormData
    }
  }, passwordConfirmationModalOpen ? /*#__PURE__*/React.createElement(PasswordConfirmation, {
    handleInputChange: handleSignageChange,
    signage: signage,
    signageErrorState: signageErrorState,
    title: i18n.t(tokens.labels.account.confirmBeneficiaryChanges)
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: firstname,
    onChange: e => handleInputChange('firstname', e.target.value),
    error: Boolean(firstnameError),
    helperText: firstnameError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: lastname,
    onChange: e => handleInputChange('lastname', e.target.value),
    error: Boolean(lastnameError),
    helperText: lastnameError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.relationship),
    value: relationship,
    fullWidth: true,
    onChange: e => handleInputChange('relationship', e.target.value, beneficiaryID),
    error: Boolean(relationshipError),
    helperText: relationshipError,
    select: true,
    selectOptions: optionsWithSpouse()
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD),
    value: birthdate,
    onChange: e => handleInputChange('birthdate', e.target.value),
    error: Boolean(birthdateError),
    helperText: birthdateError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.type),
    value: type,
    fullWidth: true,
    onChange: e => handleInputChange('type', e.target.value, beneficiaryID),
    error: Boolean(typeError),
    helperText: typeError,
    select: true,
    selectOptions: isContingent ? [i18n.t(tokens.labels.beneficiaries.contingent)] : beneficiariesOptions()
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.beneficiaries.percentOfBenefits),
    suffix: "%",
    value: shares,
    onChange: e => handleInputChange('shares', e.target.value),
    error: Boolean(sharesError),
    helperText: sharesError,
    fullWidth: true
  }), isContingent ? /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true,
    margin: "20px 0 5px 0"
  }, i18n.t(tokens.labels.beneficiaries.pleaseEnsureAllContingentBeneficiaries)) : /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true,
    margin: "20px 0 5px 0"
  }, i18n.t(tokens.labels.beneficiaries.pleaseEnsureAllPrimaryBeneficiaries)), /*#__PURE__*/React.createElement(BeneficiariesSharesInputs, {
    isContingent: isContingent,
    contingentBeneficiaries: contingentBeneficiaries,
    primaryBeneficiaries: primaryBeneficiaries,
    beneficiaryID: beneficiaryID,
    sharesError: sharesError,
    handleSharesChange: handleSharesChange
  })));
};
export default BeneficiaryModal;